import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import {
  CheckCircle, Clock, Search, Download, Eye, User, FileText, Loader2, X, Trash2, AlertTriangle, ArrowUpDown, ArrowUp, ArrowDown, Users, ChevronDown
} from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import Button from "../../../components/common/Button/Button";
import PermissionGate from "../../../components/common/PermissionGate/PermissionGate";
import { policyService, acknowledgmentService } from "../../../api/services/complianceService";
import departmentRoleService from "../../../api/services/departmentRoleService";
import hrService from "../../../api/services/hrService";
import { get } from "../../../api/apiClient";
import { API_ENDPOINTS } from "../../../api/config";
import { toast } from "../../../utils/toast";
import { formatDate } from "../../../utils/stringUtils";
import { formatIndianNumber } from "../../../utils/amountFormatter";
import {
  exportToCSV,
} from "../../../utils/exportUtils";
import ExportModal from "../../../components/common/ExportModal";
import DateRangeFilter from "../../../components/common/DateRangeFilter";
import PaginationControls from "../../../components/common/Pagination/PaginationControls";
import useUrlTableState from "../../../hooks/useUrlTableState";

const AcknowledgmentsNew = ({ onBack, userRole, initialMode, id, onClose, isDrawer }) => {
  const queryClient = useQueryClient();

  const defaultFilters = useMemo(() => ({
    page: 1,
    limit: 20,
    filterPolicy: 'all',
    filterDepartment: 'all',
    filterStartDate: '',
    filterEndDate: '',
    filterEmployee: ''
  }), []);

  const {
    filters: acknowledgmentFilters,
    updateFilter: updateAcknowledgmentFilter,
    updatePage: updateAcknowledgmentPage,
    updateLimit: updateAcknowledgmentLimit,
    resetFilters: resetAcknowledgmentFilters,
  } = useUrlTableState(defaultFilters);

  const filterPolicy = acknowledgmentFilters.filterPolicy || 'all';
  const setFilterPolicy = useCallback((policy) => {
    updateAcknowledgmentFilter({ filterPolicy: policy });
  }, [updateAcknowledgmentFilter]);

  const filterDepartment = acknowledgmentFilters.filterDepartment || 'all';
  const setFilterDepartment = useCallback((department) => {
    updateAcknowledgmentFilter({ filterDepartment: department });
  }, [updateAcknowledgmentFilter]);

  const startDate = acknowledgmentFilters.filterStartDate || '';
  const endDate = acknowledgmentFilters.filterEndDate || '';

  const setDateRange = useCallback((range) => {
    updateAcknowledgmentFilter({ filterStartDate: range.startDate || '', filterEndDate: range.endDate || '' });
  }, [updateAcknowledgmentFilter]);

  const setStartDate = useCallback((date) => {
    updateAcknowledgmentFilter({ filterStartDate: date });
  }, [updateAcknowledgmentFilter]);

  const setEndDate = useCallback((date) => {
    updateAcknowledgmentFilter({ filterEndDate: date });
  }, [updateAcknowledgmentFilter]);

  const page = acknowledgmentFilters.page;
  const pageSize = acknowledgmentFilters.limit;

  const setPage = useCallback((p) => {
    updateAcknowledgmentPage(p);
  }, [updateAcknowledgmentPage]);

  const setPageSize = useCallback((l) => {
    updateAcknowledgmentLimit(l);
  }, [updateAcknowledgmentLimit]);

  const [selectedAcknowledgment, setSelectedAcknowledgment] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [selectedRowsData, setSelectedRowsData] = useState({});
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);

  // Employee Dropdown State
  const [assigneeSearchTerm, setAssigneeSearchTerm] = useState('');
  const [debouncedAssigneeSearchTerm, setDebouncedAssigneeSearchTerm] = useState('');
  const [dropdownLimit, setDropdownLimit] = useState(10);
  const [openAssigneeDropdown, setOpenAssigneeDropdown] = useState(false);
  const [selectedAssignee, setSelectedAssignee] = useState(null);
  const assigneeDropdownRef = useRef(null);

  // Debounce employee search term
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedAssigneeSearchTerm(assigneeSearchTerm);
    }, 300);
    return () => clearTimeout(handler);
  }, [assigneeSearchTerm]);

  // Sorting state
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");

  // KPI Card Component
  const KpiCard = ({ icon: Icon, title, value, color, subtitle, trend }) => {
      let bgColor = "bg-[#0F2942]";
      if (color?.includes("brand") || color?.includes("blue")) bgColor = "bg-[#0F2942]";
      else if (color?.includes("red")) bgColor = "bg-[#DC2626]";
      else if (color?.includes("green") || color?.includes("emerald")) bgColor = "bg-[#16A34A]";
      else if (color?.includes("purple") || color?.includes("indigo")) bgColor = "bg-[#7C3AED]";
      else if (color?.includes("yellow") || color?.includes("orange") || color?.includes("amber")) bgColor = "bg-[#D97706]";

      return (
          <div className="bg-[#F8FAFC] rounded-2xl p-6 border border-gray-100 flex items-center justify-between shadow-sm transition-all duration-200 hover:shadow-md hover:border-blue-100 group">
              <div className="space-y-4 flex-1">
                  <h3 className="text-[11px] font-bold tracking-[0.1em] text-slate-500 uppercase">
                      {title}
                  </h3>
                  <div className="flex items-baseline space-x-2">
                      <span className="text-[28px] font-bold text-slate-900 leading-none tracking-tight">
                          {value}
                      </span>
                      {(subtitle || trend) && (
                          <div className="flex items-center space-x-2">
                              {subtitle && (
                                  <span className="text-sm font-medium text-slate-500">
                                      {subtitle}
                                  </span>
                              )}
                              {trend && (
                                  <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${trend.color} shadow-sm`}>
                                      {trend.label}
                                  </span>
                              )}
                          </div>
                      )}
                  </div>
              </div>
              <div className={`w-12 h-12 rounded-full ${bgColor} flex items-center justify-center text-white shadow-sm transform transition-transform duration-300 group-hover:scale-110 flex-shrink-0 ml-4`}>
                  <Icon className="w-5 h-5" />
              </div>
          </div>
      );
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (assigneeDropdownRef.current && !assigneeDropdownRef.current.contains(event.target)) {
        setOpenAssigneeDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Fetch employees for dropdown (using system-wide dropdown employees endpoint with search & limit)
  const { data: employeesResponse, isFetching: isFetchingEmployees } = useQuery({
    queryKey: ['employees', 'dropdown', dropdownLimit, debouncedAssigneeSearchTerm],
    queryFn: async () => {
      const response = await get(`${API_ENDPOINTS.HR.EMPLOYEES_DROPDOWN_SEARCH}?limit=${dropdownLimit}&search=${debouncedAssigneeSearchTerm}`);
      return response;
    },
    staleTime: 5 * 60 * 1000,
    placeholderData: (previousData) => previousData, // Keep data visible while loading more
  });

  // Handle scroll to load more
  const handleDropdownScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    // When reached near bottom (within 20px)
    if (scrollHeight - scrollTop <= clientHeight + 20) {
      if (!isFetchingEmployees) {
        setDropdownLimit(prev => prev + 10);
      }
    }
  };

  // Reset limit when search term changes
  useEffect(() => {
    setDropdownLimit(10);
  }, [assigneeSearchTerm]);

  const filterEmployee = acknowledgmentFilters.filterEmployee || '';

  // Fetch selected employee details if filterEmployee is present in URL
  const { data: selectedEmployeeResponse } = useQuery({
    queryKey: ['selected-employee', filterEmployee],
    queryFn: async () => {
      if (!filterEmployee) return null;
      const response = await get(`${API_ENDPOINTS.HR.EMPLOYEES_DROPDOWN_SEARCH}?limit=1&search=${filterEmployee}`);
      return response;
    },
    enabled: !!filterEmployee && (!selectedAssignee || selectedAssignee.employeeId !== filterEmployee),
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (selectedEmployeeResponse) {
      const payload = selectedEmployeeResponse?.data || selectedEmployeeResponse;
      const list = payload?.employees || payload?.users || [];
      if (Array.isArray(list) && list.length > 0) {
        const u = list[0];
        const firstName = u.personalInfo?.firstName || u.firstName || u.name?.split(' ')[0] || '';
        const lastName = u.personalInfo?.lastName || u.lastName || u.name?.split(' ').slice(1).join(' ') || '';
        const emp = {
          id: u.id || u._id || u.user,
          employeeId: u.employeeId || 'N/A',
          firstName,
          lastName,
          name: u.name || `${firstName} ${lastName}`.trim(),
          email: u.officialEmail || u.email || u.contactInfo?.personalEmail || '',
          department: u.department || u.employmentInfo?.department || ''
        };
        setSelectedAssignee(emp);
      }
    } else if (!filterEmployee) {
      setSelectedAssignee(null);
    }
  }, [selectedEmployeeResponse, filterEmployee]);

  // Page reset is handled by useUrlTableState

  const assigneeOptions = useMemo(() => {
    const payload = employeesResponse?.data || employeesResponse;
    const list = payload?.employees || payload?.users || [];
    if (Array.isArray(list)) {
      return list.map(u => {
        const firstName = u.personalInfo?.firstName || u.firstName || u.name?.split(' ')[0] || '';
        const lastName = u.personalInfo?.lastName || u.lastName || u.name?.split(' ').slice(1).join(' ') || '';
        return {
          id: u.id || u._id || u.user,
          employeeId: u.employeeId || 'N/A',
          firstName,
          lastName,
          name: u.name || `${firstName} ${lastName}`.trim(),
          email: u.officialEmail || u.email || u.contactInfo?.personalEmail || '',
          department: u.department || u.employmentInfo?.department || ''
        };
      });
    }
    return [];
  }, [employeesResponse]);

  const filteredAssignees = assigneeOptions; // Search is handled by the API

  // Fetch policies (Main List)
  const { data: policiesResponse, isLoading: policiesLoading } = useQuery({
    queryKey: ['policies', page, pageSize, filterPolicy, filterDepartment, startDate, endDate],
    queryFn: async () => {
      const response = await policyService.getPolicies({
        page,
        limit: pageSize,
        category: filterPolicy !== "all" ? filterPolicy : undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
      });
      return response;
    },
    staleTime: 0,
  });

  const policies = useMemo(() => {
    if (!policiesResponse) return [];
    const payload = policiesResponse?.data || policiesResponse;
    const data = Array.isArray(payload?.data) ? payload.data : Array.isArray(payload) ? payload : [];
    return data;
  }, [policiesResponse]);

  // Fetch specific employee's acknowledgments if one is selected
  const { data: employeeAcksResponse, isLoading: employeeAcksLoading } = useQuery({
    queryKey: ['employee-acknowledgments', selectedAssignee?.employeeId],
    queryFn: () => acknowledgmentService.getAcknowledgments({
      employeeId: selectedAssignee?.employeeId,
      limit: 1000
    }),
    enabled: !!selectedAssignee,
  });

  const employeeAcksMap = useMemo(() => {
    if (!employeeAcksResponse) return {};
    const payload = employeeAcksResponse?.data || employeeAcksResponse;
    const acks = Array.isArray(payload?.items) ? payload.items : Array.isArray(payload) ? payload : [];
    return acks.reduce((acc, ack) => {
      acc[ack.policyId] = ack;
      return acc;
    }, {});
  }, [employeeAcksResponse]);

  // Fetch departments for filter
  const { data: departmentsResponse } = useQuery({
    queryKey: ['departments', 'all'],
    queryFn: async () => departmentRoleService.getDepartments({ isActive: 'all' }),
    staleTime: 5 * 60 * 1000,
  });

  const allDepartments = useMemo(() => {
    if (!departmentsResponse) return [];
    const payload = departmentsResponse?.data || departmentsResponse;
    const departments = Array.isArray(payload) ? payload : Array.isArray(payload?.data) ? payload.data : [];
    return departments.map((dept) => dept.name || dept).filter(Boolean).sort();
  }, [departmentsResponse]);

  // Fetch stats for KPIs
  const { data: statsResponse } = useQuery({
    queryKey: ['acknowledgments-stats', filterDepartment, startDate, endDate],
    queryFn: async () => {
      const params = {
        department: filterDepartment !== "all" ? filterDepartment : undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
      };
      return acknowledgmentService.getAcknowledgmentStats(params);
    },
    staleTime: 0,
  });

  const kpiTotals = useMemo(() => {
    if (!statsResponse) return { total: 0, acknowledged: 0, pending: 0 };
    const payload = statsResponse?.data || statsResponse;
    const overall = payload?.overall || payload;
    return {
      total: overall?.totalAcknowledgments ?? overall?.total ?? 0,
      acknowledged: overall?.acknowledged ?? 0,
      pending: (overall?.totalAcknowledgments ?? overall?.total ?? 0) - (overall?.acknowledged ?? 0),
    };
  }, [statsResponse]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const sortedPolicies = useMemo(() => {
    if (!sortField) return policies;
    return [...policies].sort((a, b) => {
      let aV = a[sortField] || "";
      let bV = b[sortField] || "";
      if (typeof aV === 'string') aV = aV.toLowerCase();
      if (typeof bV === 'string') bV = bV.toLowerCase();
      if (sortDirection === "asc") return aV > bV ? 1 : -1;
      return aV < bV ? 1 : -1;
    });
  }, [policies, sortField, sortDirection]);

  const { totalItems, totalPages } = useMemo(() => {
    if (!policiesResponse) return { totalItems: 0, totalPages: 1 };
    const payload = policiesResponse?.data || policiesResponse;
    const pagination = payload?.pagination;
    return {
      totalItems: pagination?.totalItems || pagination?.total || policies.length,
      totalPages: pagination?.totalPages || 1,
    };
  }, [policiesResponse, policies]);

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const loading = policiesLoading || (selectedAssignee && employeeAcksLoading);

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const next = {};
      sortedPolicies.forEach(p => { next[p.id || p._id] = p; });
      setSelectedRowsData(next);
    } else {
      setSelectedRowsData({});
    }
  };

  const handleSelectRow = (p) => {
    const id = p.id || p._id;
    setSelectedRowsData(prev => {
      const next = { ...prev };
      if (next[id]) delete next[id];
      else next[id] = p;
      return next;
    });
  };

  const handleExport = () => setShowExportModal(true);

  const handleClearFilters = () => {
    setSelectedAssignee(null);
    setAssigneeSearchTerm('');
    resetAcknowledgmentFilters();
    setSortField(null);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Compliance Acknowledgments</h1>
              <p className="text-gray-600">Track employee policy acknowledgments and compliance metrics</p>
            </div>
            <div className="flex items-center space-x-4">
              <Button variant="outline" onClick={handleExport} className="flex items-center space-x-2">
                <Download className="h-4 w-4" />
                <span>Export</span>
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <KpiCard icon={FileText} title="Total Required" value={formatIndianNumber(kpiTotals.total)} color="bg-[#0F2942]" />
          <KpiCard icon={CheckCircle} title="Acknowledged" value={formatIndianNumber(kpiTotals.acknowledged)} color="bg-emerald-600" />
          <KpiCard icon={Clock} title="Pending" value={formatIndianNumber(kpiTotals.pending)} color="bg-amber-600" />
        </div>
      </div>

      {/* Filters Area */}
      <div className="px-6 mb-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
          <div className="flex flex-wrap items-center gap-4">
            {/* Employee Dropdown */}
            <div className="relative w-72" ref={assigneeDropdownRef}>
              <label className="block text-xs font-bold text-gray-500 uppercase mb-1 ml-1">Select Employee</label>
              <div
                className="flex items-center justify-between px-4 py-2 border border-gray-300 rounded-lg bg-white cursor-pointer hover:border-brand/60 transition-colors"
                onClick={() => setOpenAssigneeDropdown(!openAssigneeDropdown)}
              >
                <div className="flex items-center space-x-2 truncate">
                  <User className="h-4 w-4 text-gray-400" />
                  <span className={`text-sm truncate ${selectedAssignee ? 'text-gray-900 font-medium' : 'text-gray-400'}`}>
                    {selectedAssignee ? `${selectedAssignee.firstName} ${selectedAssignee.lastName} (${selectedAssignee.employeeId})` : 'Select Employee...'}
                  </span>
                </div>
                <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${openAssigneeDropdown ? 'rotate-180' : ''}`} />
              </div>

              {openAssigneeDropdown && (
                <div className="absolute z-50 w-full mt-2 bg-white border border-gray-200 rounded-xl shadow-2xl overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
                  <div className="p-2 border-b border-gray-100 bg-gray-50/50">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <input
                        type="text"
                        className="w-full pl-9 pr-4 py-2 text-sm border-none bg-transparent focus:ring-0 placeholder:text-gray-400"
                        placeholder="Search by name or ID..."
                        value={assigneeSearchTerm}
                        onChange={(e) => setAssigneeSearchTerm(e.target.value)}
                        autoFocus
                      />
                    </div>
                  </div>
                  <div className="max-h-60 overflow-y-auto" onScroll={handleDropdownScroll}>
                    <div
                      className="px-4 py-2 text-sm text-brand font-medium hover:bg-brand/5 cursor-pointer border-b border-gray-50 flex items-center space-x-2"
                      onClick={() => {
                        setSelectedAssignee(null);
                        updateAcknowledgmentFilter({ filterEmployee: '' });
                        setOpenAssigneeDropdown(false);
                      }}
                    >
                      <Users className="h-4 w-4" />
                      <span>All Employees</span>
                    </div>
                    {filteredAssignees.length > 0 ? (
                      filteredAssignees.map((emp) => (
                        <div
                          key={emp.employeeId}
                          className="px-4 py-2 hover:bg-gray-50 cursor-pointer transition-colors border-b border-gray-50 last:border-0"
                          onClick={() => {
                            setSelectedAssignee(emp);
                            updateAcknowledgmentFilter({ filterEmployee: emp.employeeId });
                            setOpenAssigneeDropdown(false);
                            setAssigneeSearchTerm('');
                          }}
                        >
                          <div className="text-sm font-semibold text-gray-900">{emp.firstName} {emp.lastName}</div>
                          <div className="text-xs text-gray-500 flex items-center space-x-2">
                            <span>ID: {emp.employeeId}</span>
                            <span className="w-1 h-1 rounded-full bg-gray-300"></span>
                            <span>{emp.department}</span>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="px-4 py-8 text-center text-sm text-gray-500">No employees found</div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Date Range */}
            <div className="flex-1 min-w-[300px]">
              <label className="block text-xs font-bold text-gray-500 uppercase mb-1 ml-1">Policy Created Date</label>
              <DateRangeFilter
                startDate={startDate}
                endDate={endDate}
                onChange={setDateRange}
                inputClassName="text-sm w-full px-3 py-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-1 focus:ring-brand/20 focus:border-brand/60"
              />
            </div>

            {/* Clear Button */}
            <div className="pt-5">
              <Button variant="ghost" size="sm" onClick={handleClearFilters} className="text-gray-500 hover:text-gray-700">
                <X className="h-4 w-4 mr-1" />
                <span>Reset</span>
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Table Area */}
      <div className="px-6 pb-12">
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50/80">
                <tr>
                  <th className="px-6 py-4 text-left">
                    <input type="checkbox" onChange={handleSelectAll} className="h-4 w-4 text-brand rounded focus:ring-brand" />
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider cursor-pointer group" onClick={() => handleSort("title")}>
                    <div className="flex items-center space-x-1">
                      <span>Policy Title</span>
                      <ArrowUpDown className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Category</th>
                  <th className="px-6 py-4 text-center text-xs font-bold text-gray-500 uppercase tracking-wider">Acknowledged</th>
                  <th className="px-6 py-4 text-center text-xs font-bold text-gray-500 uppercase tracking-wider">Pending</th>
                  <th className="px-6 py-4 text-center text-xs font-bold text-gray-500 uppercase tracking-wider text-red-600">Overdue</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Created At</th>
                  {selectedAssignee && <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Status</th>}
                  <th className="px-6 py-4 text-right text-xs font-bold text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {loading ? (
                  <tr>
                    <td colSpan={selectedAssignee ? 9 : 8} className="px-6 py-20 text-center">
                      <div className="flex flex-col items-center">
                        <Loader2 className="h-10 w-10 animate-spin text-brand" />
                        <span className="mt-4 text-gray-500 font-medium">Loading data...</span>
                      </div>
                    </td>
                  </tr>
                ) : sortedPolicies.length === 0 ? (
                  <tr>
                    <td colSpan={selectedAssignee ? 9 : 8} className="px-6 py-20 text-center">
                      <div className="flex flex-col items-center text-gray-400">
                        <FileText className="h-16 w-16 mb-4" />
                        <h3 className="text-lg font-bold text-gray-900">No policies found</h3>
                      </div>
                    </td>
                  </tr>
                ) : (
                  sortedPolicies.map((policy) => {
                    const policyId = policy.id || policy._id;
                    const empAck = selectedAssignee ? employeeAcksMap[policyId] : null;

                    return (
                      <tr key={policyId} className="hover:bg-gray-50/50 transition-colors group">
                        <td className="px-6 py-4">
                          <input
                            type="checkbox"
                            checked={!!selectedRowsData[policyId]}
                            onChange={() => handleSelectRow(policy)}
                            className="h-4 w-4 text-brand rounded focus:ring-brand"
                          />
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex flex-col">
                            <span
                              className="text-sm capitalize font-bold text-gray-900 group-hover:text-brand transition-colors cursor-help"
                              title={policy.title}
                            >
                              {policy.title?.length > 20 ? `${policy.title.substring(0, 20)}...` : policy.title}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className="px-2.5 py-1 rounded-md bg-gray-100 text-gray-600 text-xs font-bold uppercase">
                            {policy.category}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-center">
                          <span className="text-sm font-bold text-emerald-600">{policy.acknowledgedEmployees || 0}</span>
                        </td>
                        <td className="px-6 py-4 text-center">
                          <span className="text-sm font-bold text-amber-600">{policy.pendingEmployees || 0}</span>
                        </td>
                        <td className="px-6 py-4 text-center">
                          <span className="text-sm font-bold text-red-600">{policy.overdueEmployees || 0}</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                          {formatDate(policy.createdAt, { includeTime: false })}
                        </td>
                        {selectedAssignee && (
                          <td className="px-6 py-4">
                            {empAck ? (
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold uppercase ${empAck.status === 'acknowledged' ? 'bg-emerald-100 text-emerald-700' :
                                empAck.status === 'overdue' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'
                                }`}>
                                {empAck.status}
                              </span>
                            ) : (() => {
                              const policyStartDate = policy.effectiveDate || policy.createdAt;
                              const dueDate = new Date(policyStartDate);
                              dueDate.setDate(dueDate.getDate() + 7);
                              const isOverdue = new Date() > dueDate;

                              return (
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold uppercase ${isOverdue ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-500'}`}>
                                  {isOverdue ? 'Overdue' : 'Pending'}
                                </span>
                              );
                            })()}
                          </td>
                        )}
                        <td className="px-6 py-4 text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => { setSelectedAcknowledgment(empAck || { policyId, policyTitle: policy.title, status: 'pending' }); setShowDetails(true); }}
                            className="text-brand hover:bg-brand/10 rounded-lg"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          {/* Pagination */}
          {totalItems > 0 && (
            <PaginationControls
              currentPage={page}
              totalPages={totalPages}
              totalItems={totalItems}
              limit={pageSize}
              hasNextPage={page < totalPages}
              hasPrevPage={page > 1}
              onPageChange={setPage}
              onLimitChange={(limit) => {
                setPageSize(limit);
              }}
              limitOptions={[10, 20, 50, 100]}
              border={false}
            />
          )}
        </div>
      </div>

      {/* Details Modal */}
      {showDetails && selectedAcknowledgment && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full overflow-hidden">
            <div className="p-6 border-b border-gray-100 flex items-center justify-between bg-gray-50">
              <h3 className="text-xl font-bold text-gray-900">Acknowledgment Details</h3>
              <button onClick={() => setShowDetails(false)} className="p-2 hover:bg-gray-200 rounded-full transition-colors">
                <X className="h-6 w-6 text-gray-500" />
              </button>
            </div>
            <div className="p-8">
              <div className="grid grid-cols-2 gap-8">
                <div>
                  <label className="text-xs font-bold text-gray-400 uppercase tracking-widest">Policy</label>
                  <p className="text-lg font-bold text-gray-900 mt-1">{selectedAcknowledgment.policyTitle}</p>
                </div>
                <div>
                  <label className="text-xs font-bold text-gray-400 uppercase tracking-widest">Status</label>
                  <div className="mt-1">
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-bold uppercase ${selectedAcknowledgment.status === 'acknowledged' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                      {selectedAcknowledgment.status}
                    </span>
                  </div>
                </div>
                {selectedAssignee && (
                  <>
                    <div>
                      <label className="text-xs font-bold text-gray-400 uppercase tracking-widest">Employee</label>
                      <p className="text-gray-900 font-bold mt-1">{selectedAssignee.firstName} {selectedAssignee.lastName}</p>
                    </div>
                    <div>
                      <label className="text-xs font-bold text-gray-400 uppercase tracking-widest">Employee ID</label>
                      <p className="text-gray-900 font-bold mt-1">{selectedAssignee.employeeId}</p>
                    </div>
                  </>
                )}
              </div>
            </div>
            <div className="p-6 border-t border-gray-100 bg-gray-50 flex justify-end">
              <Button onClick={() => setShowDetails(false)} className="px-8">Close</Button>
            </div>
          </div>
        </div>
      )}

      {/* Export Modal */}
      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        onExportCurrentPage={() => { toast.success("Exporting current page..."); setShowExportModal(false); }}
        onExportRange={() => { toast.success("Exporting range..."); setShowExportModal(false); }}
        onExportAll={() => { toast.success("Exporting all..."); setShowExportModal(false); }}
        title="Compliance Report"
        loading={exportLoading}
      />
    </div>
  );
};

export default AcknowledgmentsNew;
